import { Patient } from '@dreryk/egabinet-patient-frontend-api';

import { useFormController, useTranslate } from '@dreryk/react-common';
import { Modal, ModalWidth } from '@dreryk/react-components';

import { patientConsentsAndAuthorizationsTranslationKeys } from '../../translations';
import { useMedicalDataAccessAuthorization } from '../MedicalDataAccessAuthorizationPanel/MedicalDataAccessAuthorizationPanel.hooks';
import { useLastMedicalTreatmentConsentQuery } from '../MedicalTreatmentConsentSection/MedicalTreatmentConsentSection.hooks';

import { usePrintMedicalConsentAndAuthorizations } from './MedicalConsentAndAuthorizationsPrintModal.hooks';
import { createInitialFormValues } from './MedicalConsentAndAuthorizationsPrintModal.utils';
import { AuthorizationsToPrintCheckboxGroup } from './MedicalConsentAndAuthorizationsPrintModalFields/AuthorizationsToPrintCheckboxGroup/AuthorizationsToPrintCheckboxGroup';
import { PatientConsentCheckbox } from './MedicalConsentAndAuthorizationsPrintModalFields/PatientConsentCheckbox/PatientConsentCheckbox';
import { StyledMedicalConsentAndAuthorizationPrintModal } from './StyledMedicalConsentAndAuthorizationPrintModal';
import { StyledMedicalConsentAndAuthorizationPrintModalMessage } from './StyledMedicalConsentAndAuthorizationPrintModalMessage';

type MedicalDataAccessAuthorizationModalProps = {
   readonly closeModal: () => void;
   readonly patient: Patient;
};

export type MedicalConsentAndAuthorizationPrintBean = {
   patientConsentToPrint: boolean;
   confirmedAuthorizationsToPrint: string[];
};

const MedicalConsentAndAuthorizationPrintModal = (props: MedicalDataAccessAuthorizationModalProps): JSX.Element => {
   const { data: medicalTreatmentConsent } = useLastMedicalTreatmentConsentQuery(props.patient.id);
   const { data: medicalDataAccessAuthorization } = useMedicalDataAccessAuthorization(props.patient.id);
   const { handleSubmit, register } = useFormController<MedicalConsentAndAuthorizationPrintBean>(
      createInitialFormValues(medicalDataAccessAuthorization ?? [], medicalTreatmentConsent)
   );
   const { executeAsync: printMedicalConsentAndAuthorizations } = usePrintMedicalConsentAndAuthorizations();
   const translate = useTranslate();

   const onSubmit = async (formData: MedicalConsentAndAuthorizationPrintBean): Promise<void> => {
      await printMedicalConsentAndAuthorizations({
         confirmedAuthorizationsToPrint: formData.confirmedAuthorizationsToPrint,
         patientConsentToPrint: formData.patientConsentToPrint,
         patientId: props.patient.id,
      });
      props.closeModal();
   };

   return (
      <Modal
         animateVisibility
         confirmationButtonLabel={patientConsentsAndAuthorizationsTranslationKeys.printModal.confirmAction}
         onCancel={props.closeModal}
         onCloseRequest={props.closeModal}
         onSubmit={handleSubmit(onSubmit)}
         title={patientConsentsAndAuthorizationsTranslationKeys.printModal.title}
         width={ModalWidth.NORMAL}
         visible
         wrapInForm
         testId={'medicalConsentAndAuthorizationsPrint'}
      >
         <StyledMedicalConsentAndAuthorizationPrintModal>
            <StyledMedicalConsentAndAuthorizationPrintModalMessage>
               {translate(patientConsentsAndAuthorizationsTranslationKeys.printModal.modalMessage)}
            </StyledMedicalConsentAndAuthorizationPrintModalMessage>
            <PatientConsentCheckbox patient={props.patient} register={register} />
            <AuthorizationsToPrintCheckboxGroup
               medicalDataAccessAuthorization={medicalDataAccessAuthorization}
               register={register}
            />
         </StyledMedicalConsentAndAuthorizationPrintModal>
      </Modal>
   );
};

export { MedicalConsentAndAuthorizationPrintModal };
